import { useState, useEffect } from "react";
import { Upload, CheckCircle, AlertCircle, Package } from "lucide-react";
import axios, { API, errorText } from "@/utils/api";
import { useT } from "@/contexts/LanguageContext";

const InventoryUploadView = ({ clientId, assetUuid }) => {
  const t = useT();
  const [inventory, setInventory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  // Inventory is asset-scoped: the server will not choose a building.
  const assetQuery = assetUuid ? `?asset_uuid=${assetUuid}` : "";

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await axios.get(`${API}/clients/${clientId}/inventory${assetQuery}`);
        setInventory(response.data);
      } catch (error) {
        console.error("Error fetching inventory:", error);
      }
    };
    fetchInventory();
  }, [clientId, assetQuery]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!assetUuid) {
      setMessage({ type: "danger", text: "Select an asset before uploading inventory." });
      e.target.value = "";
      return;
    }

    setUploading(true);
    setMessage(null);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API}/clients/${clientId}/inventory/upload?asset_uuid=${assetUuid}`,
        formData
      );
      setMessage({ type: "success", text: response.data.message });
      const invRes = await axios.get(`${API}/clients/${clientId}/inventory${assetQuery}`);
      setInventory(invRes.data);
    } catch (error) {
      setMessage({ type: "danger", text: errorText(error, "Error uploading file") });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">{t("upload_inventory")}</h2>
        <p className="page-subtitle">{t("upload_csv_xlsx")}</p>
      </div>

      {message && (
        <div className={`alert alert-${message.type} mb-lg`}>
          {message.type === "success" ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span>{message.text}</span>
        </div>
      )}

      {!assetUuid && (
        <div className="alert alert-warning mb-lg">
          <AlertCircle size={18} />
          <span>Select an asset in the header to upload or view inventory for a building.</span>
        </div>
      )}

      <div className="card mb-lg">
        <div className="file-upload-zone" onClick={() => document.getElementById("inv-file-input").click()}>
          <input id="inv-file-input" type="file" className="file-upload-input" accept=".csv,.xlsx,.xls" onChange={handleUpload} />
          <div className="file-upload-icon">{uploading ? <div className="spinner"></div> : <Upload size={48} strokeWidth={1.5} />}</div>
          <div className="file-upload-text">{uploading ? t("uploading") : "Drag & drop your file here or click to browse"}</div>
          <div className="file-upload-hint">Supported formats: CSV, Excel (.xlsx, .xls)</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h4 className="card-title">Inventory Items ({inventory.length})</h4>
        </div>
        {inventory.length === 0 ? (
          <div className="flex-center" style={{ padding: "var(--space-xxl)", color: "var(--text-muted)" }}>
            <div style={{ textAlign: "center" }}>
              <Package size={48} strokeWidth={1} />
              <p className="mt-md">No inventory items yet</p>
            </div>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("equipment")}</th>
                  <th>{t("department")}</th>
                  <th>{t("energy_kwh")}</th>
                  <th>{t("power_kw")}</th>
                  <th>{t("cost")}</th>
                  <th>{t("co2_kg")}</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{item.equipment_name}</td>
                    <td><span className="badge badge-neutral">{item.department?.replace(/_/g, " ")}</span></td>
                    <td>{item.energy_consumed_kwh?.toFixed(2)}</td>
                    <td>{item.power_rating_kw?.toFixed(2)}</td>
                    <td>${item.energy_cost?.toFixed(2)}</td>
                    <td>{item.co2_emission_kg?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryUploadView;
